<template>
  <div class="vevo-detail-wrapper">
    <!-- 上部ピンクバー: 戻る + "レポート" -->
    <div class="vevo-topbar">
      <v-btn
        icon
        variant="text"
        size="small"
        class="vevo-back-btn"
        @click="goBack"
        :ripple="false"
      >
        <img :src="chevronLeftIcon" alt="back" class="vevo-back-icon" />
      </v-btn>
      <div class="vevo-topbar-title">VEVOレポート</div>
      <div class="vevo-topbar-spacer"></div>
    </div>

    <div v-if="isLoading" class="d-flex justify-center pa-6">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="!detail" class="vevo-empty">
      レポートが見つかりませんでした。
    </div>

    <div v-else class="vevo-detail-body">
      <!-- 白カード -->
      <div class="vevo-detail-card">
        <!-- ヘッダー: アイコン + 日付 + タイトル -->
        <div class="vevo-detail-header">
          <div class="vevo-detail-icon-wrap">
            <img :src="envelopeIconRead" alt="" class="vevo-detail-icon" />
            <span v-if="isUnread" class="vevo-new-badge">NEW</span>
          </div>
          <div class="vevo-detail-titlewrap">
            <div class="vevo-detail-date">
              {{ formatPublishedAt(detail.publishedAt) }} 公開
            </div>
            <div class="vevo-detail-title">{{ detail.title }}</div>
          </div>
        </div>

        <!-- 上区切り線 -->
        <div class="vevo-divider" />

        <!-- データ不足注意書き (3ヶ月分揃っていない場合のみ) -->
        <div
          v-if="detail.dataCoverageMonths.length < 3"
          class="vevo-coverage-note"
        >
          {{ coverageMessage }}にデータがある場合に生成しています。
        </div>

        <!-- 本文 -->
        <span class="vevo-coverage-note caution-text">※この文章は、保育士が確認したお子さまの成長のようすをもとに作成されておりますが、AIによるデータの解釈の違いによって、間違った情報になる可能性があります。</span>
        <div class="vevo-detail-content">{{ detail.content }}</div>

        <!-- 下区切り線 -->
        <div class="vevo-divider" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import { aiReportModule } from "@/store/entityModules/aiReport";
import type { AiReportDetail, AiReportQuarter } from "@/api/ApiAiReport";

interface DataType {
  isLoading: boolean;
  envelopeIconRead: string;
  chevronLeftIcon: string;
  wasUnreadOnLoad: boolean;
}

export default defineComponent({
  name: "VevoDetail",
  data: (): DataType => ({
    isLoading: false,
    envelopeIconRead: require("@/assets/vevo/envelope_icon_read.png"),
    chevronLeftIcon: require("@/assets/vevo/chevron_left.png"),
    wasUnreadOnLoad: false,
  }),
  computed: {
    detail(): AiReportDetail | null {
      return aiReportModule.detail;
    },
    isUnread(): boolean {
      // 表示時点で「未読だったか」を保持しておく (fetchDetail で既読化されるため)
      return this.wasUnreadOnLoad;
    },
    coverageMessage(): string {
      if (!this.detail) return "";
      const baseMonth = (this.detail.quarter - 1) * 3 + 4;
      const endMonth = ((baseMonth + 2 - 1) % 12) + 1;
      const startMonth = ((baseMonth - 1) % 12) + 1;
      return `${startMonth}月～${endMonth}月`;
    },
  },
  async mounted() {
    await this.load();
  },
  watch: {
    "$route.params": {
      handler() {
        this.load();
      },
    },
  },
  methods: {
    async load() {
      const childId = parseInt(this.$route.params.childId as string, 10);
      const businessYear = parseInt(
        this.$route.params.businessYear as string,
        10,
      );
      const quarter = parseInt(
        this.$route.params.quarter as string,
        10,
      ) as AiReportQuarter;
      if (!childId || !businessYear || ![1, 2, 3, 4].includes(quarter)) return;

      // 既読化される前に未読状態をスナップショット（実データを渡すことで正しく判定される）
      const targetReport = aiReportModule.reports.find(
        (r) =>
          r.childId === childId &&
          r.businessYear === businessYear &&
          r.quarter === quarter
      );
      this.wasUnreadOnLoad = targetReport ? aiReportModule.isUnread(targetReport) : false;

      // ローディングが画面外に隠れないよう、データ取得を始める前にスクロール位置を一番上に戻す
      window.scrollTo(0, 0);
      this.isLoading = true;
      try {
        await aiReportModule.fetchDetail({ childId, businessYear, quarter });
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          window.scrollTo(0, 0);
        });
      }
    },
    goBack() {
      const childId = this.$route.params.childId;
      this.$router.push({
        path: "/vevo",
        query: { childId: childId }
      });
    },
    formatPublishedAt(value: string): string {
      return dayjs(value).format("YYYY年M月D日");
    },
  },
});
</script>

<style lang="scss" scoped>
$vevo-pink: #EE6090;
$vevo-pink-light: #FDE0E4;
$vevo-gray: #808080;
$vevo-divider: #F5B3C4;

.vevo-detail-wrapper {
  min-height: 100vh;
  padding-bottom: 60px;
}

.vevo-topbar {
  position: sticky;
  top: 0;
  z-index: 5;
  height: calc(52px + env(safe-area-inset-top));
  padding-top: env(safe-area-inset-top);
  background-color: $vevo-pink;
  display: flex;
  align-items: center;
  padding-left: 8px;
  padding-right: 8px;

  .vevo-back-btn {
    color: white !important;
    background: transparent !important;
    box-shadow: none !important;

    .vevo-back-icon {
      width: 24px;
      height: 24px;
      display: block;
    }
  }

  .vevo-topbar-title {
    flex: 1;
    text-align: center;
    color: white;
    font-size: 16px;
    font-weight: 700;
  }

  .vevo-topbar-spacer {
    width: 40px;
  }
}

.vevo-empty {
  text-align: center;
  color: white;
  font-size: 14px;
  padding: 32px 0;
}

.vevo-detail-body {
  padding: 12px 12px 0 12px;
}

.vevo-detail-card {
  background-color: white;
  border-radius: 18px;
  padding: 14px;
}

.vevo-detail-header {
  display: flex;
  align-items: center;
}

.vevo-detail-icon-wrap {
  position: relative;
  width: 70px;
  height: 70px;
  flex-shrink: 0;
}

.vevo-detail-icon {
  width: 70px;
  height: 70px;
  display: block;
}

.vevo-new-badge {
  position: absolute;
  left: 16px;
  bottom: 6px;
  background-color: $vevo-pink;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  line-height: 1.2;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.vevo-detail-titlewrap {
  flex: 1;
  margin-left: 10px;
  min-width: 0;
}

.vevo-detail-date {
  font-size: 13px;
  color: $vevo-gray;
  font-weight: 400;
  line-height: 1.4;
}

.vevo-detail-title {
  font-size: 15px;
  font-weight: 600;
  color: #000;
  line-height: 1.3;
  margin-top: 2px;
}

.vevo-divider {
  height: 1px;
  background-color: $vevo-divider;
  margin: 12px 0;
}

.vevo-coverage-note {
  font-size: 11px;
  color: $vevo-gray;
  margin-bottom: 8px;
  padding: 6px 10px;
  background-color: $vevo-pink-light;
  border-radius: 8px;
}

.vevo-detail-content {
  font-size: 13px;
  line-height: 1.8;
  color: #000;
  padding: 0 5px;
  white-space: pre-wrap;
  font-feature-settings: "palt";
}

.caution-text {
  font-style: italic;
  display: block;
  padding-left: calc(9px + 1em);
  text-indent: -1em;
  line-height: 1.3;
}
</style>
